import { isError } from '@/helpers/checkQuery'
import type { ResponseWrapper } from '@/api/types/base.types'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import {
  CRM_CHAT_PROVIDER_KEYS,
  parseCrmChatProviderKey,
} from '@pages/Admin/CRM/components/integrations/common/lib/crmChatProviderKey'
import { crmIntegrationChatApi } from '@pages/Admin/CRM/components/integrations/common/api/crm-integration-chat.api'
import type {
  CrmIntegrationChatUnreadCountData,
  CrmIntegrationChatUnreadIntegration,
} from '@pages/Admin/CRM/components/integrations/common/types/crm-integration-chat.types'
import { normalizeVkUnreadCount } from '@pages/Admin/CRM/utils/crmRealtimeWebSocket'

export type IntegrationChatUnreadByIntegrationId = Record<number, number>

export type IntegrationChatUnreadAccessibleIntegration = {
  integrationId: number
  provider: CrmChatProviderKey
  name: string
  count: number
  myCount: number
}

export type IntegrationChatGlobalUnreadSnapshot = {
  total: number
  myTotal: number
  byProvider: Record<CrmChatProviderKey, number>
  myByProvider: Record<CrmChatProviderKey, number>
  byIntegrationId: IntegrationChatUnreadByIntegrationId
  myByIntegrationId: IntegrationChatUnreadByIntegrationId
  /** Интеграции из unread-count: connected + доступны текущему юзеру (даже при count=0). */
  accessibleIntegrations: IntegrationChatUnreadAccessibleIntegration[]
}

export const EMPTY_INTEGRATION_CHAT_GLOBAL_UNREAD: IntegrationChatGlobalUnreadSnapshot =
  {
    total: 0,
    myTotal: 0,
    byProvider: {
      vkontakte: 0,
      max: 0,
      telegram: 0,
      wazzup: 0,
    },
    myByProvider: {
      vkontakte: 0,
      max: 0,
      telegram: 0,
      wazzup: 0,
    },
    byIntegrationId: {},
    myByIntegrationId: {},
    accessibleIntegrations: [],
  }

export const CRM_CHAT_UNREAD_ALL_TOOLTIP = 'Все непрочитанные'
export const CRM_CHAT_UNREAD_MINE_TOOLTIP = 'Мои непрочитанные'

const sumByProvider = (
  byProvider: Record<CrmChatProviderKey, number>,
): number =>
  CRM_CHAT_PROVIDER_KEYS.reduce((sum, provider) => sum + byProvider[provider], 0)

const normalizeUnreadProvider = (
  value: string | null | undefined,
): CrmChatProviderKey | null => {
  if (value === 'vk') return 'vkontakte'
  return parseCrmChatProviderKey(value)
}

/** Непрочитанные «мои» — только my_count (сообщения), не my_unread_chats_count (чаты). */
const readMyUnreadCount = (
  item: CrmIntegrationChatUnreadIntegration,
): number => {
  if (item.my_count != null) {
    return normalizeVkUnreadCount(item.my_count)
  }
  return 0
}

export const parseCrmIntegrationUnreadCountData = (
  data: CrmIntegrationChatUnreadCountData | null | undefined,
): IntegrationChatGlobalUnreadSnapshot => {
  if (!data || typeof data !== 'object') {
    return EMPTY_INTEGRATION_CHAT_GLOBAL_UNREAD
  }

  const byProvider = { ...EMPTY_INTEGRATION_CHAT_GLOBAL_UNREAD.byProvider }
  const myByProvider = { ...EMPTY_INTEGRATION_CHAT_GLOBAL_UNREAD.myByProvider }
  const byIntegrationId: IntegrationChatUnreadByIntegrationId = {}
  const myByIntegrationId: IntegrationChatUnreadByIntegrationId = {}
  const accessibleIntegrations: IntegrationChatUnreadAccessibleIntegration[] =
    []
  const integrations = Array.isArray(data.integrations) ? data.integrations : []

  integrations.forEach((item) => {
    const provider = normalizeUnreadProvider(item?.provider)
    const integrationId = Number(item?.integration_id)
    const count = normalizeVkUnreadCount(item?.count)
    const myCount = readMyUnreadCount(item)

    if (Number.isFinite(integrationId) && integrationId > 0) {
      byIntegrationId[integrationId] = count
      myByIntegrationId[integrationId] = myCount
    }

    if (!provider || !Number.isFinite(integrationId) || integrationId <= 0) {
      return
    }

    byProvider[provider] += count
    myByProvider[provider] += myCount
    accessibleIntegrations.push({
      integrationId,
      provider,
      name: typeof item?.name === 'string' ? item.name : String(integrationId),
      count,
      myCount,
    })
  })

  const total =
    data.total != null
      ? normalizeVkUnreadCount(data.total)
      : sumByProvider(byProvider)

  const myTotal =
    data.my_total != null
      ? normalizeVkUnreadCount(data.my_total)
      : sumByProvider(myByProvider)

  return {
    total,
    myTotal,
    byProvider,
    myByProvider,
    byIntegrationId,
    myByIntegrationId,
    accessibleIntegrations,
  }
}

export const sumIntegrationChatUnreadForIds = (
  byIntegrationId: IntegrationChatUnreadByIntegrationId,
  integrationIds: number[],
): number =>
  integrationIds.reduce(
    (sum, integrationId) => sum + (byIntegrationId[integrationId] ?? 0),
    0,
  )

/** GET /v1/crm-integration-chat/unread-count — один запрос, total + integrations[]. */
export const fetchIntegrationChatGlobalUnread =
  async (): Promise<IntegrationChatGlobalUnreadSnapshot | null> => {
    try {
      const res = await crmIntegrationChatApi.getUnreadCount()
      if (isError(res)) {
        return null
      }

      const payload = res.data as ResponseWrapper<CrmIntegrationChatUnreadCountData>
      return parseCrmIntegrationUnreadCountData(payload?.data)
    } catch {
      return null
    }
  }

/** @deprecated use fetchIntegrationChatGlobalUnread */
export const fetchIntegrationChatGlobalUnreadCount = async (): Promise<
  number | null
> => {
  const snapshot = await fetchIntegrationChatGlobalUnread()
  return snapshot?.total ?? null
}
